/**
 * <nia-gallery>
 * Visor con miniaturas y lightbox. Es el mismo en escritorio y en móvil: una
 * sola diapositiva visible y las miniaturas para cambiarla. Sobre la foto no
 * hay scroll ni arrastre; en móvil el lightbox se oculta por CSS y queda el
 * contador.
 *
 * Escucha nia:variant:change para saltar a la imagen de la variante elegida.
 * El buy box de la fase P2 emitirá ese evento con el media_id.
 */
const FOCUSABLE = 'button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

class NiaGallery extends HTMLElement {
  #index = 0;
  #lastFocus = null;

  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('[data-gal-slide]'));
    this.thumbs = Array.from(this.querySelectorAll('[data-gal-thumb]'));
    this.counter = this.querySelector('[data-gal-counter]');
    this.lightbox = this.querySelector('[data-gal-lightbox]');
    this.figure = this.querySelector('[data-gal-figure]');

    this.addEventListener('click', this.#onClick);
    document.addEventListener('keydown', this.#onKeydown);
    document.addEventListener('nia:variant:change', this.#onVariantChange);
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this.#onKeydown);
    document.removeEventListener('nia:variant:change', this.#onVariantChange);
    document.body.style.overflow = '';
  }

  get isZoomed() {
    return this.classList.contains('is-zoomed');
  }

  /** @param {number} index */
  select(index) {
    if (index < 0 || index >= this.slides.length) return;
    this.#index = index;

    this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
    this.thumbs.forEach((thumb, i) => thumb.setAttribute('aria-current', String(i === index)));

    if (this.counter) this.counter.textContent = `${index + 1}/${this.slides.length}`;
    if (this.isZoomed) this.#paintZoom();
  }

  /** Salta a la imagen asociada a una variante. */
  selectByMediaId(mediaId) {
    const index = this.slides.findIndex((slide) => slide.dataset.mediaId === String(mediaId));
    if (index >= 0) this.select(index);
  }

  #onVariantChange = (event) => {
    const mediaId = event.detail?.mediaId;
    if (mediaId) this.selectByMediaId(mediaId);
  };

  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const thumb = target.closest('[data-gal-thumb]');
    if (thumb) {
      this.select(Number(thumb.dataset.galThumb));
      return;
    }

    if (target.closest('[data-gal-close]') || target === this.lightbox) {
      this.#closeZoom();
      return;
    }

    const nav = target.closest('[data-gal-nav]');
    if (nav) {
      const total = this.slides.length;
      this.select((this.#index + Number(nav.dataset.galNav) + total) % total);
      return;
    }

    if (target.closest('[data-gal-zoom]')) this.#openZoom();
  };

  #onKeydown = (event) => {
    if (!this.isZoomed) return;

    if (event.key === 'Escape') {
      event.preventDefault();
      this.#closeZoom();
      return;
    }

    const total = this.slides.length;
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.select((this.#index - 1 + total) % total);
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.select((this.#index + 1) % total);
      return;
    }

    if (event.key !== 'Tab' || !this.lightbox) return;

    const focusable = Array.from(this.lightbox.querySelectorAll(FOCUSABLE));
    if (focusable.length === 0) return;

    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  /**
   * Crea la imagen ampliada la primera vez que se abre y toma la fuente de
   * mayor resolución que el navegador ya descargó, para no pedirla de nuevo.
   */
  #paintZoom() {
    const source = this.slides[this.#index]?.querySelector('img');
    if (!source || !this.figure) return;

    if (!this.zoomImage) {
      this.zoomImage = document.createElement('img');
      this.figure.appendChild(this.zoomImage);
    }

    this.zoomImage.src = source.currentSrc || source.src;
    this.zoomImage.alt = source.alt || '';
  }

  #openZoom() {
    // Solo tiene sentido sobre imágenes: un video ya se reproduce en su sitio.
    if (this.slides[this.#index]?.dataset.type !== 'image') return;

    this.#lastFocus = document.activeElement;
    this.#paintZoom();
    this.classList.add('is-zoomed');
    this.lightbox?.removeAttribute('inert');
    document.body.style.overflow = 'hidden';
    this.lightbox?.querySelector(FOCUSABLE)?.focus({ preventScroll: true });
  }

  #closeZoom() {
    if (!this.isZoomed) return;
    this.classList.remove('is-zoomed');
    this.lightbox?.setAttribute('inert', '');
    document.body.style.overflow = '';
    if (this.#lastFocus instanceof HTMLElement) this.#lastFocus.focus();
  }
}

if (!customElements.get('nia-gallery')) {
  customElements.define('nia-gallery', NiaGallery);
}
